<template>
  <v-container>
    <v-row
      class="mb-4"
      align="center"
    >
      <v-col>
        <v-btn
          text
          @click="$router.push('/eod/ldb-reconciliation')"
        >
          <v-icon left>
            mdi-arrow-left
          </v-icon>
          {{ t.backToList }}
        </v-btn>
        <h2 class="d-inline ml-2">
          {{ t.pageTitle }}
        </h2>
      </v-col>
      <v-col class="text-right">
        <v-btn-toggle
          v-model="lang"
          mandatory
          dense
        >
          <v-btn
            x-small
            value="lo"
          >
            ລາວ
          </v-btn>
          <v-btn
            x-small
            value="en"
          >
            EN
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <div
      v-for="batch in closedBatches"
      :key="batch.batch_id"
      class="mb-6"
    >
      <v-card
        class="batch-card"
        elevation="2"
      >
        <v-card-title class="batch-header py-3">
          <v-row
            align="center"
            no-gutters
          >
            <v-col>
              <span class="text-h6 font-weight-bold">
                {{ formatDate(batch.date_from) }} — {{ formatDate(batch.date_to) }}
              </span>
              <v-chip
                small
                label
                color="green"
                dark
                class="ml-3 font-weight-bold"
              >
                {{ t.closed }}
              </v-chip>
            </v-col>
            <v-col class="text-right text-body-2 grey--text text--darken-1">
              {{ t.runBy }}: <strong>{{ batch.reconciled_by ? batch.reconciled_by.name : '-' }}</strong>
              &nbsp;|&nbsp;
              {{ moment(batch.created_at).format("DD-MM-YYYY HH:mm") }}
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <v-simple-table
            dense
            class="summary-table mb-4"
          >
            <thead>
              <tr>
                <th>{{ t.category }}</th>
                <th class="text-right">
                  {{ t.qty }}
                </th>
                <th class="text-right">
                  {{ t.amount }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="font-weight-bold">
                  {{ t.totalQr }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ batch.total_records }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ fmtLAK(batch.total_amount) }}
                </td>
              </tr>
              <tr class="row-green">
                <td>
                  <v-icon
                    small
                    color="green"
                    class="mr-1"
                  >
                    mdi-check-circle
                  </v-icon>{{ t.matched }}
                </td>
                <td class="text-right">
                  {{ batch.matched_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.matched_amount) }}
                </td>
              </tr>
              <tr class="row-red">
                <td>
                  <v-icon
                    small
                    color="red"
                    class="mr-1"
                  >
                    mdi-alert-circle
                  </v-icon>{{ t.missed }}
                </td>
                <td class="text-right">
                  {{ batch.missed_payment_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.missed_payment_amount) }}
                </td>
              </tr>
              <tr>
                <td>
                  <v-icon
                    small
                    color="green"
                    class="mr-1"
                  >
                    mdi-check-decagram
                  </v-icon>{{ t.approved }}
                </td>
                <td class="text-right">
                  {{ batch.approved_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.approved_amount) }}
                </td>
              </tr>
              <tr class="row-amber">
                <td>
                  <v-icon
                    small
                    color="amber darken-3"
                    class="mr-1"
                  >
                    mdi-connection
                  </v-icon>{{ t.errors }}
                </td>
                <td class="text-right">
                  {{ batch.ldb_error_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.ldb_error_amount) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <!-- Settlement (read-only) -->
          <v-divider class="mb-4" />
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon
              small
              class="mr-1"
            >
              mdi-bank
            </v-icon>
            {{ t.settlement }}
          </div>
          <v-row dense>
            <v-col
              cols="12"
              sm="3"
            >
              <v-text-field
                :value="fmtNumber(batch.actual_received_amount)"
                :label="t.actualReceived"
                outlined
                class="settlement-input"
                readonly
                filled
                suffix="LAK"
              />
            </v-col>
            <v-col
              cols="12"
              sm="3"
            >
              <v-text-field
                :value="fmtNumber(batch.system_confirmed_amount)"
                :label="t.systemConfirmed"
                outlined
                class="settlement-input"
                readonly
                filled
                suffix="LAK"
              />
            </v-col>
            <v-col
              cols="12"
              sm="3"
            >
              <v-text-field
                :value="calcDiff(batch)"
                :label="t.difference"
                outlined
                class="settlement-input"
                readonly
                filled
                suffix="LAK"
                :class="diffClass(batch)"
              />
            </v-col>
            <v-col
              cols="12"
              sm="3"
            >
              <v-textarea
                :value="batch.comment || ''"
                :label="t.comment"
                outlined
                class="settlement-input"
                readonly
                filled
                rows="1"
                auto-grow
              />
            </v-col>
          </v-row>
          <div class="text-caption grey--text">
            <v-icon
              x-small
              class="mr-1"
            >
              mdi-lock
            </v-icon>
            {{ t.closedBy }}: {{ batch.closed_by ? batch.closed_by.name : '-' }}
            | {{ batch.closed_at ? moment(batch.closed_at).format("DD-MM-YYYY HH:mm") : '' }}
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            small
            color="#49A3DA"
            dark
            @click="viewBatch(batch)"
          >
            <v-icon
              small
              left
            >
              mdi-eye
            </v-icon>
            {{ t.viewDetails }}
          </v-btn>
          <v-btn
            small
            color="green"
            dark
            @click="exportBatch(batch.batch_id)"
          >
            <v-icon
              small
              left
            >
              mdi-download
            </v-icon>
            {{ t.export }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <v-card
      v-if="!loading && closedBatches.length === 0"
      class="text-center pa-8"
    >
      <v-icon
        size="64"
        color="grey lighten-1"
      >
        mdi-history
      </v-icon>
      <p class="text-h6 grey--text mt-4">
        {{ t.noHistory }}
      </p>
    </v-card>
  </v-container>
</template>

<script>
import moment from 'moment';

export default {
  name: 'LdbReconciliationHistory',
  title() {
    return 'Vientiane Waste Co-Dev | EOD History';
  },
  data() {
    return {
      lang: 'lo',
      translations: {
        lo: {
          pageTitle: 'ປະຫວັດການກວດສອບ EOD',
          backToList: 'ກັບຄືນ',
          closed: 'ປິດແລ້ວ',
          runBy: 'ແລ່ນໂດຍ',
          category: 'ລາຍການ',
          qty: 'ຈຳນວນ',
          amount: 'ມູນຄ່າ (LAK)',
          totalQr: 'ລວມ QR ທັງໝົດ',
          matched: 'ກົງກັນ',
          missed: 'ຂາດການຊຳລະ',
          approved: 'ອະນຸມັດແລ້ວ',
          errors: 'ຜິດພາດ LDB',
          settlement: 'ການຕັ້ງຖານ',
          actualReceived: 'ຈຳນວນທີ່ໄດ້ຮັບຕົວຈິງ',
          systemConfirmed: 'ລະບົບຢືນຢັນ',
          difference: 'ຜົນຕ່າງ',
          comment: 'ໝາຍເຫດ',
          closedBy: 'ປິດໂດຍ',
          viewDetails: 'ເບິ່ງລາຍລະອຽດ',
          export: 'ສົ່ງອອກ',
          noHistory: 'ບໍ່ມີປະຫວັດການກວດສອບ',
        },
        en: {
          pageTitle: 'EOD Reconciliation History',
          backToList: 'Back',
          closed: 'Closed',
          runBy: 'Run By',
          category: 'Category',
          qty: 'Qty',
          amount: 'Amount (LAK)',
          totalQr: 'Total QR',
          matched: 'Matched',
          missed: 'Missed Payment',
          approved: 'Approved',
          errors: 'LDB Errors',
          settlement: 'Settlement',
          actualReceived: 'Actual Received',
          systemConfirmed: 'System Confirmed',
          difference: 'Difference',
          comment: 'Comment',
          closedBy: 'Closed by',
          viewDetails: 'View Details',
          export: 'Export',
          noHistory: 'No closed reconciliation batches',
        },
      },
      batches: [],
      loading: false,
    };
  },
  computed: {
    t() {
      return this.translations[this.lang];
    },
    closedBatches() {
      return this.batches.filter((b) => b.status === 'closed');
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    moment,
    formatDate(d) {
      if (!d) return '-';
      return moment(d).format('DD-MM-YYYY');
    },
    fmtLAK(val) {
      if (val === null || val === undefined) return '-';
      return `₭${Intl.NumberFormat().format(Math.round(Number(val)))}`;
    },
    fmtNumber(val) {
      if (val === null || val === undefined || val === '') return '-';
      return Intl.NumberFormat().format(Math.round(Number(val)));
    },
    calcDiff(batch) {
      if (!batch.actual_received_amount) return '-';
      const diff = Number(batch.actual_received_amount) - Number(batch.system_confirmed_amount || 0);
      return Intl.NumberFormat().format(Math.round(diff));
    },
    diffClass(batch) {
      if (!batch.actual_received_amount) return '';
      const diff = Number(batch.actual_received_amount) - Number(batch.system_confirmed_amount || 0);
      return diff === 0 ? 'diff-zero' : 'diff-nonzero';
    },
    fetchData() {
      this.loading = true;
      this.$store.commit('Loading_State', true);
      this.$axios
        .get('ldb-reconciliation')
        .then((res) => {
          this.loading = false;
          this.$store.commit('Loading_State', false);
          if (res.data.code == 200 || res.data.success) {
            const data = res.data.data;
            const list = Array.isArray(data) ? data : (data.data || []);
            list.sort((a, b) => new Date(b.date_from || b.created_at) - new Date(a.date_from || a.created_at));
            this.batches = list;
          }
        })
        .catch(() => {
          this.loading = false;
          this.$store.commit('Loading_State', false);
        });
    },
    viewBatch(batch) {
      this.$router.push(`/eod/ldb-reconciliation/${batch.batch_id}`);
    },
    exportBatch(batchId) {
      this.$axios
        .get(`ldb-reconciliation/${batchId}/export`)
        .then((res) => {
          if ((res.data.code == 200 || res.data.success) && res.data.data.download_link) {
            window.open(res.data.data.download_link, '_blank');
          }
        })
        .catch(() => {
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to export',
          });
        });
    },
  },
};
</script>

<style scoped>
.batch-card {
  border-radius: 8px !important;
  border-left: 4px solid green;
}
.batch-header {
  background-color: #fafafa;
}
</style>

<style>
.summary-table th {
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  color: #555 !important;
  background-color: #f5f5f5 !important;
}
.summary-table td {
  font-size: 15px !important;
  color: #222 !important;
  font-weight: 500 !important;
  padding: 8px 16px !important;
}
.summary-table .row-green td { background-color: #f0faf0; }
.summary-table .row-red td { background-color: #fff5f5; }
.summary-table .row-amber td { background-color: #fffbf0; }
</style>
